export const libraries = ["places", "directions"];

export const BANGALORE_AIRPORT = { lat: 13.1986, lng: 77.7066 };

export const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

export const REQUIRED_ASTERISK_SX = {
  color: "#d32f2f",
  ml: 0.5,
};

export const getDefaultFormData = () => ({
  fullName: "",
  studentId: "",
  grade: "",
  sec: "",
  classTeacher: "",
  parentId: "",
  parentName: "",
  parentPhone: "",
  routeId: "",
  vehicleId: "",
  pickupStopId: "",
  dropStopId: "",
  pickupStopName: "",
  dropStopName: "",
  driverName: "",
  attendantName: "",
  driverId: null,
  employeeId: null,
  nfcUid: "",
  profileImage: null,
});

export const normalizeIdValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

export const getVehicleSeatsLeftDisplay = (vehicle) => {
  const seatCapacity =
    vehicle?.seatCapacity ?? vehicle?.vehicleSeatCapacity ?? "";

  if (seatCapacity === "" || seatCapacity === null) {
    return "-";
  }

  const numericSeatCapacity = Number(seatCapacity);
  const assignedStudents = Array.isArray(vehicle?.students)
    ? vehicle.students.length
    : Number(vehicle?.studentCount || 0);

  if (Number.isNaN(numericSeatCapacity)) {
    return "-";
  }

  return Math.max(numericSeatCapacity - assignedStudents, 0);
};

export const getVehicleAssignedStudentCount = (vehicle, currentStudentId) => {
  if (Array.isArray(vehicle?.students)) {
    return vehicle.students.filter(
      (student) => String(student?.id) !== String(currentStudentId || ""),
    ).length;
  }

  const studentCount = Number(vehicle?.studentCount || 0);
  return Number.isNaN(studentCount) ? 0 : studentCount;
};

export const getVehicleSeatCapacityError = (
  vehicle,
  currentStudentId,
  t = (key) => key,
) => {
  const seatCapacity =
    vehicle?.seatCapacity ?? vehicle?.vehicleSeatCapacity ?? "";

  if (seatCapacity === "" || seatCapacity === null) {
    return "";
  }

  const numericSeatCapacity = Number(seatCapacity);
  if (Number.isNaN(numericSeatCapacity)) {
    return "";
  }

  const assignedStudents = getVehicleAssignedStudentCount(
    vehicle,
    currentStudentId,
  );

  return assignedStudents >= numericSeatCapacity
    ? t("Seat full")
    : "";
};

export const formatVehicleOptionLabel = (
  vehicle,
  { includeVehicleNumber = false } = {},
) => {
  const vehicleName =
    vehicle?.name || vehicle?.VehicleNumber || vehicle?.device_id || vehicle?.id;
  const vehicleNumber = vehicle?.vehicleNumber || vehicle?.VehicleNumber || "";
  const baseLabel =
    includeVehicleNumber && vehicleNumber && vehicleNumber !== vehicleName
      ? `${vehicleName} - ${vehicleNumber}`
      : vehicleName;
  const seatsLeftDisplay = getVehicleSeatsLeftDisplay(vehicle);

  return seatsLeftDisplay === "-"
    ? baseLabel
    : `${baseLabel} (Seats left: ${seatsLeftDisplay})`;
};

const getMeaningfulAssignmentName = (...values) =>
  values.find(
    (value) =>
      typeof value === "string" &&
      value.trim() !== "" &&
      value.trim() !== "Not Assigned",
  ) || "";

const getPersonDisplayName = (person) => {
  if (!person) {
    return "";
  }

  const explicitName =
    String(person.fullName || person.full_name || person.name || "").trim();
  const firstName = String(person.firstName || person.first_name || "").trim();
  const lastName = String(person.lastName || person.last_name || "").trim();
  const combinedName = `${firstName} ${lastName}`.trim();

  return combinedName || explicitName;
};

const getPrimaryVehicleAttendant = (vehicle) => {
  if (!vehicle) {
    return null;
  }

  const groupedAttendants =
    (Array.isArray(vehicle.employees) && vehicle.employees) ||
    (Array.isArray(vehicle.employee) && vehicle.employee) ||
    (Array.isArray(vehicle.attendants) && vehicle.attendants) ||
    [];

  return groupedAttendants[0] || vehicle.employee || vehicle.attendant || null;
};

export const getVehicleAssignmentDetails = (vehicle) => {
  if (!vehicle) {
    return {
      driverName: "",
      attendantName: "",
      driverId: null,
      employeeId: null,
    };
  }

  const driver = vehicle.driver || vehicle.Driver || null;
  const attendant = getPrimaryVehicleAttendant(vehicle);
  const driverId = normalizeIdValue(vehicle.driverId ?? driver?.id) || null;
  const employeeId =
    normalizeIdValue(
      vehicle.employeeId ?? vehicle.attendantId ?? attendant?.id,
    ) || null;

  return {
    driverName: getPersonDisplayName(driver) || "Not Assigned",
    attendantName: getPersonDisplayName(attendant) || "Not Assigned",
    driverId,
    employeeId,
  };
};

export const normalizeInitialFormData = (initialData) => {
  if (!initialData) {
    return getDefaultFormData();
  }

  const vehicleAssignmentDetails = getVehicleAssignmentDetails(
    initialData.vehicle,
  );
  const driverName = getMeaningfulAssignmentName(
    initialData.driverName,
    getPersonDisplayName(initialData.driver),
    vehicleAssignmentDetails.driverName,
  );
  const attendantName = getMeaningfulAssignmentName(
    initialData.attendantName,
    getPersonDisplayName(initialData.employee),
    vehicleAssignmentDetails.attendantName,
  );

  return {
    ...getDefaultFormData(),
    ...initialData,
    fullName:
      initialData.fullName ||
      `${initialData.firstName || ""} ${
        initialData.lastName && initialData.lastName !== "."
          ? initialData.lastName
          : ""
      }`.trim(),
    sec: initialData.sec || initialData.section || "",
    parentId: initialData.parentId || initialData.parent?.parentId || "",
    parentName:
      initialData.parentName ||
      `${initialData.parent?.firstName || ""} ${
        initialData.parent?.lastName && initialData.parent.lastName !== "."
          ? initialData.parent.lastName
          : ""
      }`.trim(),
    parentPhone:
      initialData.parentPhone || initialData.parent?.phoneNumber || "",
    routeId: normalizeIdValue(initialData.routeId ?? initialData.route?.id),
    vehicleId: normalizeIdValue(
      initialData.vehicleId ?? initialData.vehicle?.id,
    ),
    pickupStopId: normalizeIdValue(
      initialData.pickupStopId ??
        initialData.pickupStop?.id ??
        initialData.pickupStop?.stopId,
    ),
    pickupStopName:
      initialData.pickupStopName || initialData.pickupStop?.name || "",
    dropStopId: normalizeIdValue(
      initialData.dropStopId ??
        initialData.dropStop?.id ??
        initialData.dropStop?.stopId,
    ),
    dropStopName: initialData.dropStopName || initialData.dropStop?.name || "",
    driverId:
      normalizeIdValue(
        initialData.driverId ??
          initialData.driver?.id ??
          vehicleAssignmentDetails.driverId,
      ) || null,
    employeeId: normalizeIdValue(
      initialData.employeeId ??
        initialData.employee?.id ??
        vehicleAssignmentDetails.employeeId,
    ) || null,
    driverName:
      driverName ||
      initialData.driverName ||
      vehicleAssignmentDetails.driverName ||
      "",
    attendantName:
      attendantName ||
      initialData.attendantName ||
      vehicleAssignmentDetails.attendantName ||
      "",
    nfcUid: initialData.nfcUid || "",
  };
};

export const matchesStopSelection = (
  stop,
  selectedStopId,
  selectedStopName = "",
) => {
  const normalizedSelectedId = Number(selectedStopId);
  const normalizedSelectedName = String(selectedStopName || "")
    .trim()
    .toLowerCase();

  if (!Number.isNaN(normalizedSelectedId)) {
    const isIdMatch =
      Number(stop?.id) === normalizedSelectedId ||
      Number(stop?.stopId) === normalizedSelectedId;

    if (isIdMatch) {
      return true;
    }
  }

  return (
    normalizedSelectedName !== "" &&
    String(stop?.name || "")
      .trim()
      .toLowerCase() === normalizedSelectedName
  );
};

export const resolveStopOptionValue = (
  stops,
  selectedStopId,
  selectedStopName = "",
) => {
  if (!Array.isArray(stops)) {
    return selectedStopId;
  }

  if (!selectedStopId && !selectedStopName) {
    return selectedStopId;
  }

  if (!selectedStopId && selectedStopName) {
    const matchedByNameOnly = stops.find((stop) =>
      matchesStopSelection(stop, selectedStopId, selectedStopName),
    );

    return matchedByNameOnly ? matchedByNameOnly.id : selectedStopId;
  }

  if (!selectedStopId) {
    if (!selectedStopName) {
      return selectedStopId;
    }
  }

  const matchedStop = stops.find((stop) =>
    matchesStopSelection(stop, selectedStopId, selectedStopName),
  );

  return matchedStop ? matchedStop.id : selectedStopId;
};

export const buildUniqueRouteStops = (stops) => {
  const uniqueStops = [];
  const seenStops = new Set();

  stops
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((stop) => {
      const stopKey = stop.id ?? stop.stopId;
      if (!seenStops.has(stopKey)) {
        seenStops.add(stopKey);
        uniqueStops.push(stop);
      }
    });

  return uniqueStops;
};

const normalizeStopName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const areCoordinatesClose = (a, b) => Math.abs(Number(a) - Number(b)) < 0.0001;

export const mapRouteCoordinatesToOrganizationStops = (
  routeCoordinates = [],
  organizationStops = [],
) => {
  if (!Array.isArray(routeCoordinates) || !Array.isArray(organizationStops)) {
    return [];
  }

  const mappedStops = routeCoordinates
    .filter(
      (coordinate) =>
        coordinate &&
        coordinate.stopName &&
        coordinate.latitude !== null &&
        coordinate.longitude !== null,
    )
    .map((coordinate) => {
      const matchedStop =
        organizationStops.find(
          (stop) =>
            normalizeStopName(stop?.name) ===
            normalizeStopName(coordinate.stopName),
        ) ||
        organizationStops.find(
          (stop) =>
            areCoordinatesClose(stop?.latitude, coordinate.latitude) &&
            areCoordinatesClose(stop?.longitude, coordinate.longitude),
        );

      return {
        id: matchedStop?.id ?? coordinate.id,
        stopId: matchedStop?.stopId ?? matchedStop?.id ?? coordinate.id,
        name: matchedStop?.name || coordinate.stopName || "Unnamed Stop",
        latitude: parseFloat(matchedStop?.latitude ?? coordinate.latitude),
        longitude: parseFloat(matchedStop?.longitude ?? coordinate.longitude),
        coordinateId: coordinate.id,
        order: coordinate.order,
        isStop: true,
      };
    });

  return buildUniqueRouteStops(mappedStops);
};

export const ensureStudentStopsInRouteStops = (
  stops = [],
  formData = {},
  initialData = null,
) => {
  const mergedStops = Array.isArray(stops) ? [...stops] : [];

  const fallbackStops = [
    initialData?.pickupStop
      ? {
          id: initialData.pickupStop.id,
          stopId: initialData.pickupStop.stopId ?? initialData.pickupStop.id,
          name:
            initialData.pickupStop.name ||
            formData.pickupStopName ||
            "Pickup Stop",
          latitude: parseFloat(initialData.pickupStop.latitude),
          longitude: parseFloat(initialData.pickupStop.longitude),
          order: -1,
          isStop: true,
        }
      : null,
    initialData?.dropStop
      ? {
          id: initialData.dropStop.id,
          stopId: initialData.dropStop.stopId ?? initialData.dropStop.id,
          name:
            initialData.dropStop.name || formData.dropStopName || "Drop Stop",
          latitude: parseFloat(initialData.dropStop.latitude),
          longitude: parseFloat(initialData.dropStop.longitude),
          order: Number.MAX_SAFE_INTEGER,
          isStop: true,
        }
      : null,
  ].filter(Boolean);

  fallbackStops.forEach((fallbackStop) => {
    const alreadyExists = mergedStops.some((stop) =>
      matchesStopSelection(stop, fallbackStop.id, fallbackStop.name),
    );

    if (!alreadyExists) {
      mergedStops.push(fallbackStop);
    }
  });

  return buildUniqueRouteStops(mergedStops);
};

export const getVehiclesForRoute = (
  routeId,
  routeVehicles = [],
  vehicles = [],
  currentVehicleId = "",
) => {
  if (!routeId) {
    return [];
  }

  const normalizedRouteId = Number(routeId);
  const mergedVehiclesById = new Map();

  const mergeVehicle = (vehicle) => {
    if (!vehicle?.id) {
      return;
    }

    const existingVehicle = mergedVehiclesById.get(Number(vehicle.id)) || {};
    const nextVehicle = { ...existingVehicle };

    Object.entries(vehicle).forEach(([key, value]) => {
      if (value !== undefined) {
        nextVehicle[key] = value;
      }
    });

    mergedVehiclesById.set(Number(vehicle.id), nextVehicle);
  };

  if (Array.isArray(routeVehicles)) {
    routeVehicles.forEach(mergeVehicle);
  }

  vehicles.forEach((vehicle) => {
    if (
      Number(vehicle?.routeId) === normalizedRouteId ||
      Number(vehicle?.id) === Number(currentVehicleId)
    ) {
      mergeVehicle(vehicle);
    }
  });

  return Array.from(mergedVehiclesById.values());
};

export const validateField = (field, value, t = (key) => key) => {
  switch (field) {
    case "fullName":
      if (!value || value.trim() === "") return t("Full name is required");
      if (value.trim().length < 2) {
        return t("Full name must be at least 2 characters");
      }
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        return t("Full name must contain only letters");
      }
      return "";
    case "studentId":
      if (!value || value.trim() === "") return t("Student ID is required");
      if (!/^[a-zA-Z0-9-_]+$/.test(value.trim())) {
        return t(
          "Student ID can contain only letters, numbers, hyphen, and underscore",
        );
      }
      return "";
    case "grade":
      if (!value || value.trim() === "") return t("Grade is required");
      if (!/^[a-zA-Z0-9]+$/.test(value.trim())) {
        return t("Grade can contain only letters and numbers");
      }
      return "";
    case "sec":
      if (!value || value.trim() === "") return t("Section is required");
      if (!/^[a-zA-Z0-9]+$/.test(value.trim())) {
        return t("Section can contain only letters and numbers");
      }
      return "";
    case "classTeacher":
      if (!value || value.trim() === "") return t("Class teacher is required");
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        return t("Class teacher must contain only letters");
      }
      return "";
    case "parentId":
      if (!value || value.trim() === "") return t("Parent ID is required");
      if (!/^[a-zA-Z0-9-_]+$/.test(value.trim())) {
        return t(
          "Parent ID can contain only letters, numbers, hyphen, and underscore",
        );
      }
      return "";
    case "parentName":
      if (!value || value.trim() === "") return t("Parent name is required");
      if (value.trim().length < 2) {
        return t("Parent name must be at least 2 characters");
      }
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        return t("Parent name must contain only letters");
      }
      return "";
    case "parentPhone":
      if (!value || value.trim() === "") {
        return t("Parent's phone number is required");
      }
      if (!/^[0-9]{10}$/.test(value.replace(/\s/g, ""))) {
        return t("Please enter a valid 10-digit phone number");
      }
      return "";
    case "routeId":
      return !value || value === "" ? t("Route is required") : "";
    case "vehicleId":
      return !value || value === "" ? t("Assigned vehicle is required") : "";
    case "pickupStopId":
      return !value || value === "" ? t("Pickup point is required") : "";
    case "dropStopId":
      return !value || value === "" ? t("Drop point is required") : "";
    default:
      return "";
  }
};

export const validateAllFields = (formData, t = (key) => key) => {
  const newErrors = {};
  [
    "fullName",
    "studentId",
    "grade",
    "sec",
    "classTeacher",
    "parentId",
    "parentName",
    "parentPhone",
    "routeId",
    "vehicleId",
    "pickupStopId",
    "dropStopId",
  ].forEach((field) => {
    const error = validateField(field, formData[field], t);
    if (error) newErrors[field] = error;
  });
  return newErrors;
};

export const getParentNameParts = (parentName) => {
  const nameParts = parentName.trim().split(/\s+/);

  if (nameParts.length === 1) {
    return {
      parentFirstName: nameParts[0],
      parentLastName: ".",
    };
  }

  return {
    parentFirstName: nameParts[0],
    parentLastName: nameParts.slice(1).join(" "),
  };
};

export const buildStudentData = (formData, parentId, initialData, isEdit) => {
  const studentData = {
    fullName: formData.fullName,
    studentId: formData.studentId,
    grade: formData.grade,
    sec: formData.sec,
    classTeacher: formData.classTeacher,
    parentId: parseInt(parentId),
    routeId: parseInt(formData.routeId),
    vehicleId: parseInt(formData.vehicleId),
    pickupStopId: parseInt(formData.pickupStopId),
    dropStopId: parseInt(formData.dropStopId),
    driverId: formData.driverId ? parseInt(formData.driverId) : null,
    employeeId: formData.employeeId ? parseInt(formData.employeeId) : null,
  };

  if (isEdit && initialData) {
    studentData.id = initialData.id;
  }

  return studentData;
};

export const getStopsToDisplay = (formData, routes, initialData = null) => {
  if (!formData.routeId) return [];

  const selectedRoute = routes.find((r) => r.id === parseInt(formData.routeId));
  if (!selectedRoute || !selectedRoute.stops) return [];

  const routeStops = ensureStudentStopsInRouteStops(
    selectedRoute.stops,
    formData,
    initialData,
  );

  if (formData.pickupStopId && formData.dropStopId) {
    const pickupIndex = routeStops.findIndex((stop) =>
      matchesStopSelection(
        stop,
        formData.pickupStopId,
        formData.pickupStopName,
      ),
    );
    const dropIndex = routeStops.findIndex((stop) =>
      matchesStopSelection(stop, formData.dropStopId, formData.dropStopName),
    );

    if (pickupIndex !== -1 && dropIndex !== -1 && pickupIndex !== dropIndex) {
      const startIndex = Math.min(pickupIndex, dropIndex);
      const endIndex = Math.max(pickupIndex, dropIndex);
      const stopsSlice = routeStops.slice(startIndex, endIndex + 1);

      return pickupIndex > dropIndex ? [...stopsSlice].reverse() : stopsSlice;
    }
  }

  return [];
};

export const getSelectedRouteColor = (routeId, routes) => {
  if (!routeId) return "#4285F4";
  const selectedRoute = routes.find((r) => r.id === parseInt(routeId));
  return selectedRoute?.routeColor || "#4285F4";
};
